import { UserStatCard } from "@/components/dashboard/user-stat-card";
import { BordersTab } from "@/components/profiles/borders-tab";
import { useStatsFeedback } from "@/components/profiles/stats-feedback";
import { StatsTab } from "@/components/profiles/stats-tab";
import { TitlesTab } from "@/components/profiles/titles-tab";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useAchievementRewards,
  useAchievements,
} from "@/data/achievements/hooks";
import { useUpdateUserProfile, useUserProfile } from "@/data/users/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { AchievementReward } from "../../data/achievements/types";
import { useCurrentUser } from "../../data/auth/hooks";

export const Route = createFileRoute("/profiles/customize")({
  component: CustomizeProfileComponent,
});

function CustomizeProfileComponent() {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const { data: userProfile, isLoading: isProfileLoading } = useUserProfile(
    currentUser?.id
  );
  const { data: achievementsData, isLoading: isAchievementsLoading } =
    useAchievements();
  const { data: unlockedRewards, isLoading: isRewardsLoading } =
    useAchievementRewards();

  const updateUserProfile = useUpdateUserProfile();
  const { showFeedback, setShowFeedback, showFeedbackIfNeeded } =
    useStatsFeedback();

  const [selectedBorder, setSelectedBorder] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(
    "TITLE_ACTIVE_MEMBER"
  );
  const [activeTab, setActiveTab] = useState("borders");

  // Update selected border and title when user profile is loaded
  useEffect(() => {
    if (userProfile?.user) {
      setSelectedBorder(userProfile.user.borderId || null);
      setSelectedTitle(userProfile.user.titleId || "TITLE_ACTIVE_MEMBER");
    }
  }, [userProfile]);

  // Show feedback when stats tab is active if it hasn't been submitted yet
  useEffect(() => {
    if (activeTab === "stats") {
      showFeedbackIfNeeded();
    }
  }, [activeTab, showFeedbackIfNeeded]);

  // Handle border selection
  const handleBorderSelect = (borderId: string | null) => {
    setSelectedBorder(borderId);

    // Update user profile with selected border
    updateUserProfile.mutate(
      {
        borderId: borderId || "BORDER_DEFAULT",
      },
      {
        onSuccess: () => {
          // Invalidate the user profile query to refresh the display
          queryClient.invalidateQueries({
            queryKey: ["userProfile", currentUser?.id],
          });
        },
      }
    );
  };

  // Handle title selection
  const handleTitleSelect = (titleId: string | null) => {
    setSelectedTitle(titleId);

    // Update user profile with selected title
    updateUserProfile.mutate(
      {
        titleId: titleId || "TITLE_ACTIVE_MEMBER",
      },
      {
        onSuccess: () => {
          // Invalidate the user profile query to refresh the display
          queryClient.invalidateQueries({
            queryKey: ["userProfile", currentUser?.id],
          });
        },
      }
    );
  };

  // Check if a reward is unlocked
  const isRewardUnlocked = (rewardId: string) => {
    // Free titles are always unlocked
    if (
      rewardId === "TITLE_ACTIVE_MEMBER" ||
      rewardId === "TITLE_FITNESS_ROOKIE" ||
      rewardId === "TITLE_ENTHUSIAST"
    ) {
      return true;
    }

    // Check if the reward is in the unlocked rewards from the hook
    return unlockedRewards?.includes(rewardId as AchievementReward) || false;
  };

  // Get requirement description for a reward
  const getRequirementDescription = (rewardId: string) => {
    if (!achievementsData) return null;

    // Find the achievement that unlocks this reward
    const achievement = Object.values(achievementsData).find((a) =>
      a.rewards?.includes(rewardId as AchievementReward)
    );

    if (!achievement) return null;

    return achievement.description;
  };

  // Loading state
  if (isProfileLoading || isAchievementsLoading || isRewardsLoading) {
    return (
      <div className="p-4 bg-gradient-to-br from-[#2b7fff]/10 to-[#fe9a00]/10 w-full h-full">
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant="outline"
            onClick={() => navigate({ to: "/profiles" })}
            className="hover:bg-blue-100 hover:cursor-pointer"
          >
            <ArrowLeft className="h-5 w-5 text-[#2b7fff]" />{" "}
            <p className="bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
              Back to profiles
            </p>
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <Skeleton className="h-[400px] w-full rounded-md" />
          </div>
          <div className="lg:col-span-3">
            <Skeleton className="h-[400px] w-full rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gradient-to-br from-[#2b7fff]/10 to-[#fe9a00]/10 w-full h-full">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Preview column */}
        <div className="lg:col-span-1">
          <Button
            variant="outline"
            className="w-full mb-5 hover:bg-blue-100 hover:cursor-pointer"
            onClick={() => navigate({ to: "/profiles" })}
          >
            <ArrowLeft className="h-5 w-5 text-[#2b7fff]" />
            <p className="bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
              Back to profiles
            </p>
          </Button>

          <div className="bg-white rounded-lg shadow p-4">
            <UserStatCard userId={currentUser?.id || ""} />
          </div>
        </div>

        {/* Customization column */}
        <div className="lg:col-span-3">
          <Tabs
            defaultValue="borders"
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3 bg-gray-200">
              <TabsTrigger value="borders">Borders</TabsTrigger>
              <TabsTrigger value="titles">Titles</TabsTrigger>
              <TabsTrigger value="stats">Stats</TabsTrigger>
            </TabsList>

            {/* Borders Tab */}
            <TabsContent value="borders" className="min-h-[400px] mt-2">
              <BordersTab
                selectedBorder={selectedBorder}
                handleBorderSelect={handleBorderSelect}
                isRewardUnlocked={isRewardUnlocked}
                getRequirementDescription={getRequirementDescription}
              />
            </TabsContent>

            {/* Titles Tab */}
            <TabsContent value="titles" className="min-h-[400px] mt-2">
              <TitlesTab
                selectedTitle={selectedTitle}
                handleTitleSelect={handleTitleSelect}
                isRewardUnlocked={isRewardUnlocked}
                getRequirementDescription={getRequirementDescription}
              />
            </TabsContent>

            {/* Stats Tab */}
            <TabsContent value="stats" className="min-h-[400px] mt-2">
              <StatsTab
                showFeedback={showFeedback}
                setShowFeedback={setShowFeedback}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
